// ── Covenant Exceptions — all exceptions grouped by company ──
// First page extracted out of App.js. App-level deps (api, go) come from useApp().
import { useState, useEffect, useMemo } from "react";
import { B } from "../../../shared/theme";
import { fmtDate, localFilter, exportCSV } from "../../../shared/format";
import { Spinner, ErrorMsg, SectionHeader, Btn, SearchBar, Card, Th, Td, Badge, PageLink } from "../../../shared/ui";
import { useApp } from "../../../shared/appContext";

export const AllCovenantExceptionsPage = () => {
  const { api, rowNav } = useApp();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [q, setQ] = useState("");
  const [recentOnly, setRecentOnly] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("getCovenantExceptions")
      .then(d => { setRows(Array.isArray(d) ? d : []); setLoading(false); })
      .catch(e => { setErr(e.message); setLoading(false); });
  };
  useEffect(() => { load(); }, []);

  // sort/recency key for a calendar entry: completed date, else test date
  const dkey = e => (e.completeddate || e.testdate || "");

  // "Most recent only": per account, keep exceptions from the latest calendar entry
  const recentFiltered = useMemo(() => {
    if (!recentOnly) return rows;
    const latest = {};
    rows.forEach(e => {
      const k = e.accountid;
      const d = dkey(e);
      if (!latest[k] || d > latest[k].d) latest[k] = { d, ccid: e.covenantcalendarid };
    });
    return rows.filter(e => latest[e.accountid]?.ccid === e.covenantcalendarid);
  }, [rows, recentOnly]);

  const filtered = localFilter(recentFiltered, q, ["companyname","covenantname","issue","pmresponse","status"]);

  // group by company name, most-recent exception first within each group
  const groups = useMemo(() => {
    const by = {};
    filtered.forEach(e => { const name = e.companyname || "—"; (by[name] = by[name] || []).push(e); });
    return Object.keys(by).sort((a,b)=>a.localeCompare(b)).map(name => ({
      company: name,
      rows: by[name].slice().sort((a,b)=> dkey(b).localeCompare(dkey(a))),
    }));
  }, [filtered]);

  const totalCount = filtered.length;

  if (loading) return <Spinner />;
  if (err) return <ErrorMsg msg={err} onRetry={load} />;

  const COLS = 7;

  return <>
    <SectionHeader actions={[
      <Btn key="recent" variant={recentOnly ? "primary" : undefined} onClick={()=>setRecentOnly(o=>!o)}>
        {recentOnly ? "✓ Most recent only" : "Most recent only"}
      </Btn>,
      <SearchBar key="s" value={q} onChange={setQ} placeholder="Search exceptions..." />,
      <Btn key="ex" onClick={()=>exportCSV(filtered,["companyname","covenantname","testdate","duedate","completeddate","status","issue","pmresponse"],"covenant_exceptions.csv")}>Export CSV</Btn>
    ]} />

    <Card style={{ overflowX:"auto" }}>
      <table style={{ width:"100%", borderCollapse:"collapse", whiteSpace:"nowrap" }}>
        <thead><tr>
          <Th>Covenant</Th>
          <Th>Test Date</Th>
          <Th>Due Date</Th>
          <Th>Completed</Th>
          <Th>Status</Th>
          <Th>Issue</Th>
          <Th>PM Response</Th>
        </tr></thead>
        <tbody>
          {groups.length === 0
            ? <tr><td colSpan={COLS} style={{ textAlign:"center", color:"var(--color-text-secondary)", padding:20 }}>No exceptions.</td></tr>
            : groups.map(g => ([
                <tr key={"hdr-"+g.company}>
                  <td colSpan={COLS} style={{ padding:"8px 12px", fontSize:11, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.06em", color:B.cyanDark, background:B.cyanLight }}>
                    {g.company} ({g.rows.length})
                  </td>
                </tr>,
                ...g.rows.map(e => (
                  <tr key={e.id} style={{ cursor:"pointer" }}
                    {...rowNav("account", { accountid:e.accountid, companyname:e.companyname })}
                    onMouseEnter={ev=>ev.currentTarget.style.background="var(--color-background-secondary)"}
                    onMouseLeave={ev=>ev.currentTarget.style.background=""}>
                    <Td style={{ fontWeight:500 }}><PageLink page="account" params={{ accountid:e.accountid, companyname:e.companyname }}>{e.covenantname||"—"}</PageLink></Td>
                    <Td>{fmtDate(e.testdate)}</Td>
                    <Td>{fmtDate(e.duedate)}</Td>
                    <Td>{e.completeddate ? fmtDate(e.completeddate) : "—"}</Td>
                    <Td><Badge s={e.status||"—"} /></Td>
                    <Td style={{ maxWidth:340, whiteSpace:"normal" }}>{e.issue||"—"}</Td>
                    <Td style={{ maxWidth:340, whiteSpace:"normal" }}>{e.pmresponse||"—"}</Td>
                  </tr>
                ))
              ]))}
          <tr style={{ background:"var(--color-background-secondary)" }}>
            <td colSpan={COLS} style={{ padding:"10px 12px", fontSize:12, fontWeight:600, borderTop:"1.5px solid var(--color-border-secondary)" }}>
              {groups.length} account{groups.length!==1?"s":""} · {totalCount} exception{totalCount!==1?"s":""}
            </td>
          </tr>
        </tbody>
      </table>
    </Card>
  </>;
};
